import { Box, useTheme } from '@mui/material'
import { useLargeur } from './GrapheColonnes.js'
import { couleursCategories } from './palette.js'

/**
 * Mini-courbe de TENDANCE, à côté d'un chiffre.
 *
 * ⭐ Elle ne se lit pas, elle se regarde : ni axe, ni graduation, ni valeur.
 * Elle dit « ça monte, ça baisse, c'est irrégulier » — le chiffre exact reste
 * celui de la carte, et le détail, dans le graphe qu'on ouvre d'un clic.
 *
 * ⚠️ Un trou (`null`) coupe la ligne au lieu de la faire tomber à zéro : un
 * jour sans vente n'a pas de panier moyen, il n'a pas un panier nul.
 */
export function Sparkline({
  valeurs,
  titre,
  hauteur = 40,
  couleur,
}: {
  valeurs: readonly (number | null)[]
  /** Ce que montre la courbe, pour un lecteur d'écran. */
  titre: string
  hauteur?: number
  couleur?: string
}): JSX.Element {
  const theme = useTheme()
  const sombre = theme.palette.mode === 'dark'
  const trait = couleur ?? couleursCategories(sombre)[0] ?? theme.palette.primary.main
  const [ref, largeur] = useLargeur()

  const nombres = valeurs.filter((v): v is number => v !== null)
  const min = Math.min(0, ...nombres)
  const max = Math.max(...nombres, min + 1)
  // Marges : le point final et son anneau ne doivent pas être rognés.
  const m = 4
  const x = (i: number): number =>
    valeurs.length <= 1 ? largeur / 2 : m + (i / (valeurs.length - 1)) * (largeur - 2 * m)
  const y = (v: number): number => m + (1 - (v - min) / (max - min)) * (hauteur - 2 * m)

  // Segments continus : chaque `null` ouvre un nouveau tronçon.
  const troncons: { i: number; v: number }[][] = []
  let courant: { i: number; v: number }[] = []
  valeurs.forEach((v, i) => {
    if (v === null) {
      if (courant.length > 0) troncons.push(courant)
      courant = []
    } else courant.push({ i, v })
  })
  if (courant.length > 0) troncons.push(courant)

  const dernier = [...valeurs.entries()].reverse().find(([, v]) => v !== null)

  return (
    <Box ref={ref} sx={{ width: '100%', height: hauteur }}>
      {largeur > 0 && nombres.length > 0 ? (
        <svg width={largeur} height={hauteur} role="img" aria-label={titre}>
          {troncons.map((t, k) => {
            const ligne = t.map((p, j) => `${j === 0 ? 'M' : 'L'}${x(p.i)},${y(p.v)}`).join(' ')
            const aire = `${ligne} L${x(t.at(-1)!.i)},${y(min)} L${x(t[0]!.i)},${y(min)} Z`
            return (
              <g key={k}>
                {/* Un voile à 10 % : la surface se devine, elle ne pèse pas. */}
                <path d={aire} fill={trait} opacity={0.1} />
                <path
                  d={ligne}
                  fill="none"
                  stroke={trait}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              </g>
            )
          })}
          {dernier ? (
            <circle
              cx={x(dernier[0])}
              cy={y(dernier[1] as number)}
              r={3.5}
              fill={trait}
              stroke={theme.palette.background.paper}
              strokeWidth={2}
            />
          ) : null}
        </svg>
      ) : null}
    </Box>
  )
}
